import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { marked } from "marked";
import { api, fmtDate } from "../lib/api";
import { Seo } from "../components/site/Seo";
import { PageHeader, DraftNotice } from "../components/site/Blocks";

const esc = (s) => s.replace(/</g, "&lt;");
export const renderLegal = (md) => marked.parse(esc(md || ""), { async: false })
  .replace(/<a href="(?!https:\/\/|\/(?!\/)|mailto:)[^"]*"/g, "<a ")
  .replace(/\[\[NEDOSTAJE: ([^\]]+)\]\]/g, '<span class="rounded-sm bg-amber-100 px-1.5 font-mono text-[13px] text-amber-950" data-testid="legal-placeholder">[NEDOSTAJE: $1]</span>')
  .replace(/\[\[PREGLED: ([^\]]+)\]\]/g, '<span class="rounded-sm bg-sky-50 px-1.5 text-[13px] text-sky-900" data-testid="legal-review-note">[Pravni pregled: $1]</span>');

function CorrectionsArchive() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/public/corrections").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  return (
    <section className="mt-12 border-t-2 border-ink pt-6" aria-labelledby="corr-archive-h" data-testid="corrections-archive">
      <h2 id="corr-archive-h" className="font-display text-2xl font-semibold">Javni registar ispravki</h2>
      {items === null ? <p className="mt-4 text-sm">Učitavanje…</p> : items.length === 0 ? (
        <p className="mt-4 text-muted-foreground" data-testid="corrections-empty">Trenutno nema evidentiranih objavljenih ispravki.</p>
      ) : (
        <ol className="mt-4 space-y-5">{items.map((c) => (
          <li key={c.id} className="border-l-4 border-amber-500 pl-4" data-testid={`correction-record-${c.id}`}>
            <Link to={c.article_url} className="font-semibold underline">{c.article_headline}</Link>
            <p className="mt-1 text-sm text-muted-foreground">Ispravljeno <time dateTime={c.corrected_at}>{fmtDate(c.corrected_at)}</time> · prvobitno objavljeno <time dateTime={c.article_published_at}>{fmtDate(c.article_published_at)}</time></p>
            <p className="mt-1">{c.public_note}</p>
          </li>))}</ol>
      )}
    </section>
  );
}

export default function LegalPage({ slug }) {
  const { version } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setD(null);
    api.get(version ? `/legal/${slug}/v/${version}` : `/legal/${slug}`).then((r) => setD(r.data)).catch(() => setErr(true));
  }, [slug, version]);
  if (err) return <div className="container-news py-16">Stranica nije pronađena.</div>;
  if (!d) return <div className="container-news py-16 text-sm">Učitavanje…</div>;
  return (
    <div className="container-news max-w-3xl pb-16" data-testid={`legal-page-${slug}`}>
      <Seo title={d.title} description={d.title} noindex={slug === "impressum"} />
      <PageHeader kicker="Pravno" title={<span lang="de">{d.title}</span>} intro="" />
      <p className="mt-2 text-sm text-muted-foreground" data-testid="legal-version">Verzija {d.version} · važi od {fmtDate(d.effective_from)}{d.archived ? " · arhivirana verzija" : ""}</p>
      {d.archived && <p className="mt-2 text-sm"><Link to={`/${slug}`} className="underline">Pogledaj važeću verziju</Link></p>}
      {(d.missing?.length > 0 || !d.legal_reviewed) && !d.archived && (
        <div className="mt-4"><DraftNotice><strong>NACRT – NIJE PRAVNO PREGLEDANO.</strong> {d.missing?.length ? `Nedostaju podaci: ${d.missing.join(", ")}.` : "Tekst opisuje stvarne funkcije portala, ali još nije pravno pregledan."}</DraftNotice></div>)}
      <div className="legal-body mt-6 font-serif text-[1.05rem] leading-relaxed [&_blockquote]:border-l-4 [&_blockquote]:border-rule [&_blockquote]:pl-4 [&_a]:underline [&_h2]:mt-10 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-semibold [&_h3]:mt-6 [&_h3]:font-display [&_h3]:text-lg [&_h3]:font-semibold [&_li]:mt-1 [&_p]:mt-4 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-6" dangerouslySetInnerHTML={{ __html: renderLegal(d.content) }} />
      {slug === "korrekturen" && !d.archived && <CorrectionsArchive />}
      {slug === "kuendigung" && <p className="mt-10"><Link to="/vertraege-kuendigen" className="inline-flex h-12 items-center rounded-sm bg-pulse px-8 text-sm font-bold text-white" data-testid="kuendigung-button">Verträge hier kündigen</Link></p>}
      {!d.archived && d.versions?.length > 1 && (
        <section className="mt-12 border-t border-rule pt-4 text-sm" data-testid="legal-versions"><p className="font-bold">Prethodne verzije</p>
          <ul className="mt-2 space-y-1">{d.versions.slice(1).map((v) => <li key={v.version}><Link to={`/${slug}/verzija/${v.version}`} className="underline">Verzija {v.version}</Link> · važila od {fmtDate(v.effective_from)}{v.note ? ` · ${v.note}` : ""}</li>)}</ul></section>)}
    </div>
  );
}
